'use client'

import _ from 'lodash'
import { Fragment, Key, useState } from 'react'
import styled, { css } from 'styled-components'

import reportAnalytics from '@/app/2023/consejo/components/analyticsEvents'
import ICandidato from '@/app/2023/consejo/types/ICandidato'
import { Lista } from '@/app/2023/consejo/types/listas'
import listas from '@/app/2023/consejo/types/listas'
import partidos from '@/app/2023/consejo/types/partidos'
import { colors, spaces } from '@/config/global-parameters'
import { threePaddedNumberFormatter } from '@/helpers/formatters'

import ShowMoreBase from '../../../../../components/ShowMore'
import { IFilters } from '../Filter/types'
import Divider from './Divider'
import Header, { IHeader } from './Header'
import NamedNumber from './NamedNumber'
import Parties from './Parties'

export interface IByListSummary {
  className?: string
  candidates: ICandidato[]
  selected: ICandidato[]
  setList: (list: Lista) => void
  filters: IFilters
}

const { format: threeFormat } = threePaddedNumberFormatter

export const getByListSummary = ({
  filters,
  selected,
}: {
  selected: ICandidato[]
  filters: IFilters
}) => {
  const candidatesByList = _.groupBy(selected, ({ lista }) => lista)
  return _.orderBy(Object.entries(listas), ([, { order }]) => order)
    .map(([listaId, lista]) => {
      const candidatos = candidatesByList[listaId] || []
      const candidatosPorPartido = _.countBy(candidatos, ({ partido }) =>
        partido.replace('IND-', '')
      )
      return {
        ...lista,
        id: listaId as Lista,
        opaque: Boolean(filters.lista && listaId !== filters.lista),
        parties: lista.parties
          .filter((partyName) => !partyName.includes('IND-'))
          .map((id) => ({
            shortName: partidos[id].acronym,
            size: candidatosPorPartido[id] || 0,
          }))
          .sort(({ size: sizeA }, { size: sizeB }) => sizeB - sizeA),
        size: candidatos.length || 0,
      }
    })
    .sort(({ order: orderA }, { order: orderB }) => orderA - orderB)
}

const ByListSummary = ({ candidates, className, filters, selected, setList }: IByListSummary) => {
  const [showMore, setShowMore] = useState(false)
  const summaries = getByListSummary({ filters, selected })
  const listasSummaries = summaries.filter(({ id, name }) => name && id !== '5')
  const indigenasSummary = summaries.filter(({ id, name }) => name && id === '5')
  const handleShowMore = () => {
    setShowMore(!showMore)
    if (!showMore) {
      reportAnalytics('show_more_event', 'Resumen por lista')
    }
  }

  const realListsKeys = Object.keys(listas)
    .filter((key) => key)

  return (
    <Wrapper className={className}>
      <ResultsTable>
        <Lists
          count={realListsKeys.length}
          showMore={showMore}
        >
          {listasSummaries.map(({ id, ...summary }) => (
            <List
              key={id}
              onClick={() => setList(id)}
              showMore={showMore}
              {...summary}
            />
          ))}
          <Divider showMore={showMore} />
          {indigenasSummary.map(({ id, ...summary }) => (
            <List
              key={id}
              onClick={() => setList(id)}
              showMore={showMore}
              {...summary}
            />
          ))}
          <Total
            name={`de ${candidates.length}`}
            number={threeFormat(selected.length)}
          />
        </Lists>
      </ResultsTable>
      <ShowMore
        isOpen={showMore}
        onClick={handleShowMore}
      />
    </Wrapper>
  )
}

interface IListSummary extends IHeader {
  key: Key
  parties: {
    shortName: string
    size: number
  }[]
}

const List = ({
  color,
  key,
  name,
  onClick,
  opaque,
  parties,
  shortName,
  showMore,
  size,
}: IListSummary) => (
  <Fragment key={key}>
    <Header
      color={color}
      name={name}
      onClick={onClick}
      opaque={opaque}
      shortName={shortName}
      showMore={showMore}
      size={size}
    />
    {showMore && (
      <Parties
        opaque={opaque}
        parties={parties}
      />
    )}
  </Fragment>
)

const ResultsTable = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
`

const Total = styled(NamedNumber)`
  max-height: 36px;
  background: ${colors.colorGrey24};
  padding: ${spaces.xs} ${spaces.s};
  justify-content: center;
  width: min-content;
  align-items: center;
  @media (max-width: ${spaces.tabletBreakpoint}) {
    display: none;
  }
`

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${spaces.xs};
  height: min-content;
`

const showMoreStyle = css`
  grid-auto-flow: column;
  row-gap: 0px;
`

const Lists = styled.div<{ count: number; showMore: boolean }>`
  display: grid;
  grid: min-content min-content / repeat(${({ count }) => count - 1}, 1fr) 1px 1fr min-content;
  gap: ${spaces.s};
  width: 100%;
  @media (max-width: ${spaces.tabletBreakpoint}) {
    grid: max-content min-content / repeat(${({ count }) => count - 1}, 1fr) 1px 1fr;
    ${({ showMore }) => showMore && showMoreStyle}
  }
  ${({ showMore }) => showMore && showMoreStyle}
`

const ShowMore = styled(ShowMoreBase)`
  margin-top: -${spaces.s};
`

export default ByListSummary
